/**
 * Product Bundle Patterns Web Component
 *
 * - Single-select pattern picker (native radio group). Clicking the
 *   already-selected pattern again deselects it.
 * - When a pattern with more than one variant is selected, a matching
 *   "Pattern Size" pill group appears; patterns with a single variant skip
 *   the size step entirely.
 * - Inserts the selected pattern's fabric-specific "finished garment" image as
 *   a real, new first slide in the main product gallery — the original
 *   product image is left untouched and simply shifts to the second slot
 *   (native `<media-gallery>` prepend behaviour, the same mechanism Dawn's own
 *   colour-swatch media swap already uses) — and removes it again on
 *   deselect. This is a real gallery slide, not a src-swap on the existing
 *   image, so it participates correctly in hover-zoom and full-resolution
 *   srcset like any other slide.
 * - Replaces the main price display (#variantMainPrice) with the combined
 *   fabric+pattern total using server-precomputed, currency-converted
 *   strings (data-total-formatted, rendered via the theme's `convert-price`
 *   snippet) — never formats money itself, so the multi-currency conversion
 *   the store relies on is never bypassed. Restores the original price when
 *   deselected, and re-applies the total after a same-page variant switch,
 *   which would otherwise overwrite it with the new variant's own price.
 * - Persists the selection (sessionStorage) so it survives a colour switch
 *   (a full navigation to a different product for that colourway) and
 *   re-asserts the pattern slide as active after a same-page variant switch
 *   (e.g. yard size), which Dawn's own `updateMedia()` would otherwise
 *   silently override (it prepends the new variant's own slide instead).
 * - Intercepts main Add to Cart to add the fabric + selected pattern variant
 *   in one request; blocks submission with an inline error if a pattern
 *   needing a size is selected but no size has been chosen yet.
 *
 * Known limitation: the inserted pattern slide is only wired up for the
 * gallery's own hover-zoom (re-runs the theme's `enableZoomOnHover()` once the
 * slide lands in the DOM) — it isn't wired into the separate lightbox modal
 * (`snippets/product-media-modal.liquid`), which is keyed off `product.media`
 * ids the pattern photo doesn't have. Opening the lightbox while a pattern is
 * selected still shows the fabric's own media set.
 */
(function () {
  'use strict';
  const STORAGE_KEY = 'vlisco:bundlePatternSelection';

  class ProductBundlePatterns extends HTMLElement {
    #sectionId = '';
#baseTotalFormatted = '';
#currentFabricVariantId = null;
    /** @type {HTMLInputElement|null} */
    #selectedPatternRadio = null;
    /** @type {HTMLInputElement|null} */
    #selectedSizeRadio = null;
    /** @type {HTMLElement|null} */
    #sizeFieldset = null;
    /** @type {HTMLElement|null} */
    #sizeError = null;
    /** @type {HTMLElement|null} */
    #genericError = null;
    /** @type {HTMLElement|null} */
    #mainPriceEl = null;
    /** @type {HTMLFormElement|null} */
    #productForm = null;
    /** @type {HTMLInputElement|null} */
    #quantityInput = null;
    /** @type {HTMLElement|null} The <media-gallery> custom element for this section */
    #mediaGallery = null;
    /** data-media-id of the slide that was active before any pattern was ever selected */
    #originalMediaId = null;
    /** data-media-id of the pattern slide currently inserted in the gallery, if any */
    #activePatternMediaId = null;
    /** Stores the original #variantMainPrice innerHTML before any swap */
    #originalMainPriceHTML = null;
    /** Unsubscribe fn for the theme's variantChange pub/sub */
    #variantChangeUnsubscribe = null;
    #abortController = new AbortController();

    connectedCallback() {
      this.#sectionId = this.dataset.sectionId || '';
      this.#baseTotalFormatted = this.dataset.baseTotalFormatted || '';
      this.#sizeFieldset = this.querySelector('[ref="patternSizeFieldset"]');
      this.#sizeError = this.querySelector('[ref="patternSizeError"]');
      this.#genericError = this.querySelector('[ref="patternGenericError"]');
      this.#mainPriceEl = document.getElementById('variantMainPrice');
      this.#productForm =
        document.querySelector('#product-form-' + this.#sectionId) ||
        document.querySelector('form[action="/cart/add"]');
      this.#quantityInput = document.querySelector('#Quantity-' + this.#sectionId);
      this.#currentFabricVariantId =
  this.#productForm?.querySelector('[name="id"]')?.value || null;
      this.#mediaGallery = document.getElementById('MediaGallery-' + this.#sectionId);
      this.#originalMediaId =
        this.#mediaGallery?.querySelector('.product__media-item.is-active[data-media-id]')?.dataset.mediaId || null;
      this.#bindEvents();
      this.#restoreSelection();
    }

    disconnectedCallback() {
      this.#abortController.abort();
      if (this.#variantChangeUnsubscribe) this.#variantChangeUnsubscribe();
    }

    #bindEvents() {
      const { signal } = this.#abortController;
      this.addEventListener('click', this.#handlePatternRadioClick, { signal });
      this.addEventListener('change', this.#handleChange, { signal });
      
      if (this.#productForm) {
        // Capture-phase + document target: Dawn's own <product-form> registers its
        // submit handler directly on this same form element, and it loads (and thus
        // binds) before this deferred script does — a bubble-phase listener here
        // would always lose that race. Listening on `document` with capture:true runs
        // before the form's own at-target listeners fire, so we can actually block it.
        document.addEventListener(
          'submit',
          (e) => {
            if (e.target === this.#productForm) this.#handleFormSubmit(e);
          },
          { signal, capture: true }
        );
      }

      // Dawn's VariantSelects.updateMedia() prepends the new variant's own real
      // slide to the front on every same-page variant switch (e.g. yard size),
      // which would silently bump our pattern slide out of the active/first spot.
      // Re-assert it once that swap has landed (the slide itself is still in the
      // DOM — no need to re-insert it, just re-activate it).
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
       this.#variantChangeUnsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
  if (event.data.sectionId !== this.#sectionId) return;

  this.#currentFabricVariantId =
    event.data.variant?.id?.toString() ||
    this.#productForm?.querySelector('[name="id"]')?.value ||
    null;

  if (this.#selectedPatternRadio && this.#activePatternMediaId) {
    this.#withMediaGallery((gallery) =>
      gallery.setActiveMedia(this.#activePatternMediaId, true, false)
    );
  }

  this.#updatePriceDisplay({ refreshBaseline: true });
});
      }
    }

    // ── Events ────────────────────────────────────────────────────────────────
    /**
     * Clicking an already-selected pattern radio deselects it (native radios can't do
     * this alone). Chrome re-asserts checked=true on an already-checked radio right
     * after this click handler runs — even with preventDefault() called — when the
     * click was forwarded from its <label>, so unchecking synchronously here gets
     * silently overwritten. Deferring past that forced re-check (any macrotask) makes
     * it stick.
     */
    #handlePatternRadioClick = (e) => {
      const input = e.target.closest('input[ref="patternRadios[]"]');
      if (!input) return;
      if (this.#selectedPatternRadio === input) {
        e.preventDefault();
        setTimeout(() => {
          input.checked = false;
          this.#selectedPatternRadio = null;
          this.#onPatternDeselected();
        }, 0);
      }
    };

    #handleChange = (e) => {
      const patternInput = e.target.closest('input[ref="patternRadios[]"]');

      if (patternInput) {
        this.#selectedPatternRadio = patternInput;
        this.#onPatternSelected(patternInput);
        return;
      }

      const sizeInput = e.target.closest('input[ref="patternSizeRadios[]"]');

      if (sizeInput) {
        this.#selectedSizeRadio = sizeInput;
        this.#hideSizeError();
        this.#updatePriceDisplay();
        this.#saveSelection();
      }
    };

    #handleFormSubmit = async (e) => {
      if (!this.#selectedPatternRadio) return;

      const requiresSize = this.#selectedPatternRadio.dataset.hasSizes === 'true';

      if (requiresSize && !this.#selectedSizeRadio) {
        e.preventDefault();
        e.stopPropagation();
        this.#showSizeError();
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      this.#hideGenericError();

      const submitBtn = this.#productForm.querySelector('[type="submit"]');
      const mainVariantInput = this.#productForm.querySelector('[name="id"]');
      const mainVariantId = mainVariantInput ? parseInt(mainVariantInput.value, 10) : null;
      const quantity = this.#quantityInput ? parseInt(this.#quantityInput.value, 10) || 1 : 1;
      const patternVariantId = requiresSize
        ? parseInt(this.#selectedSizeRadio.value, 10)
        : parseInt(this.#selectedPatternRadio.dataset.variantId, 10);

      const items = [];

      if (mainVariantId) {
        items.push({ id: mainVariantId, quantity });
      }

      if (patternVariantId) {
        items.push({ id: patternVariantId, quantity: 1 });
      }

      if (submitBtn) {
        submitBtn.setAttribute('disabled', true);
        submitBtn.setAttribute('aria-disabled', 'true');
      }

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items }),
          signal: this.#abortController.signal,
        });

        if (!res.ok) throw new Error('Cart error ' + res.status);
        // This theme's own minicart (assets/minicart.js, VliscoCart) is a bespoke
        // implementation, not Dawn's <cart-drawer>/<cart-notification> — neither of
        // those tags exist on the page, so calling their APIs is a silent no-op. Its
        // own product-form.js triggers a refresh + open this exact way (a single
        // event with no detail needed; the listener re-fetches the header section
        // itself), so mirror that instead of the unrelated Dawn convention.
        document.dispatchEvent(new CustomEvent('minicart:refresh-open'));
        this.#resetSelection();
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.error('[product-bundle-patterns] ATC error', err);
        // No auto-retry here: this listener is registered on `document` (capture
        // phase, see #bindEvents), not on the form itself, so the previous
        // `removeEventListener('submit', this.#handleFormSubmit)` + requestSubmit()
        // "fallback" never actually detached anything — the retry's submit event
        // came straight back to this same handler. On a real failure (e.g. a 429)
        // that's an infinite loop hammering /cart/add.js, which is exactly what
        // deepened the rate-limit that caused this in the first place. Surface the
        // error instead and let the shopper decide to press the button again.
        this.#showGenericError();
      } finally {
        if (submitBtn) {
          submitBtn.removeAttribute('disabled');
          submitBtn.removeAttribute('aria-disabled');
        }
      }
    };

    // ── Core logic ────────────────────────────────────────────────────────────
    #onPatternSelected(input) {
      this.#hideSizeError();
      this.#showPatternMedia(input);
      this.#selectedSizeRadio = null;
      this.#showSizePanelFor(input);
      this.#updatePriceDisplay();
      this.#saveSelection();
    }

    #onPatternDeselected() {
      this.#hideSizeError();
      this.#selectedSizeRadio = null;
      this.#hideAllSizePanels();
      this.#restoreOriginalMedia();
      this.#updatePriceDisplay();
      this.#clearSavedSelection();
    }

    #showSizePanelFor(input) {
      this.#hideAllSizePanels();
      const requiresSize = input.dataset.hasSizes === 'true';
      if (!requiresSize) {
        if (this.#sizeFieldset) this.#sizeFieldset.style.display = 'none';
        return;
      }
      const panel = this.querySelector('[data-pattern-size-panel="' + input.dataset.handle + '"]');
      if (!panel) {
        if (this.#sizeFieldset) this.#sizeFieldset.style.display = 'none';
        return;
      }
      // The panel is itself a .pattern-items grid container (see the
      // snippet's <style>), not just a transparent grouping wrapper, so
      // showing it is a plain display swap — no display:contents trick needed.
      panel.style.display = 'grid';
      if (this.#sizeFieldset) this.#sizeFieldset.style.display = '';
    }

    #hideAllSizePanels() {
      this.querySelectorAll('[ref="patternSizePanels[]"]').forEach((panel) => {
        panel.style.display = 'none';
        panel.querySelectorAll('input[ref="patternSizeRadios[]"]').forEach((radio) => {
          radio.checked = false;
        });
      });
      if (this.#sizeFieldset) this.#sizeFieldset.style.display = 'none';
    }

    #showSizeError() {
      if (!this.#sizeError) return;
      this.#sizeError.hidden = false;
      this.#sizeError.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    #showGenericError() {
      if (!this.#genericError) return;
      this.#genericError.hidden = false;
      this.#genericError.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    #hideGenericError() {
      if (this.#genericError) this.#genericError.hidden = true;
    }

    #hideSizeError() {
      if (this.#sizeError) this.#sizeError.hidden = true;
    }

    /** Custom element instance methods (like MediaGallery.setActiveMedia) only exist
     *  once customElements.define() has actually run for that tag — and assets/media-
     *  gallery.js's own <script> tag sits further down the page than this component's
     *  (see sections/main-product.liquid), so on a fresh page load, restoreSelection()
     *  running synchronously in connectedCallback can reach here before media-gallery.js
     *  has executed, when #mediaGallery is still a plain, un-upgraded HTMLElement with
     *  no setActiveMedia method at all. A later user click is never affected (by then
     *  every deferred script has long since run), so retry on the next tick instead of
     *  restructuring script load order just for this one early-page-load case. */
    #withMediaGallery(fn) {
      if (!this.#mediaGallery) return;
      if (typeof this.#mediaGallery.setActiveMedia === 'function') {
        fn(this.#mediaGallery);
      } else {
        setTimeout(() => this.#withMediaGallery(fn), 0);
      }
    }

    // ── Gallery slide insertion ─────────────────────────────────────────────────
    /** Clones this pattern's hidden slide/thumbnail <template>s (rendered server-side
     *  in snippets/product-bundle-patterns.liquid, one per pattern with a resolvable
     *  image) into the real gallery lists, if not already there. No-ops if the slide
     *  is already present (e.g. re-asserting after a same-page variant switch). */
    #insertPatternMediaIfNeeded(mediaId) {
      if (!mediaId || document.getElementById('Slide-' + mediaId)) return;

      const slideTemplate = this.querySelector(`template[data-pattern-slide-template="${mediaId}"]`);
      const gallerySlider = document.getElementById('Slider-Gallery-' + this.#sectionId);
      if (!slideTemplate || !gallerySlider) return;

      gallerySlider.appendChild(slideTemplate.content.cloneNode(true));

      const thumbTemplate = this.querySelector(`template[data-pattern-thumbnail-template="${mediaId}"]`);
      const thumbList = document.getElementById('Slider-Thumbnails-' + this.#sectionId);
      if (thumbTemplate && thumbList) {
        thumbList.appendChild(thumbTemplate.content.cloneNode(true));

        const insertedThumbButton = thumbList.querySelector(`[data-target="${mediaId}"] button`);
        insertedThumbButton?.addEventListener('click', () => {
          this.#withMediaGallery((gallery) => gallery.setActiveMedia(mediaId, false));
        });
      }

      const modalTemplate = this.querySelector(`template[data-pattern-modal-template="${mediaId}"]`);
      const modalContent = document.querySelector(`#ProductModal-${this.#sectionId} .product-media-modal__content`);
      if (modalTemplate && modalContent && !modalContent.querySelector(`[data-media-id="${mediaId}"]`)) {
        modalContent.prepend(modalTemplate.content.cloneNode(true));
      }

      if (typeof enableZoomOnHover === 'function') enableZoomOnHover(2);
    }

    #removeInsertedPatternMedia(mediaId) {
      if (!mediaId) return;
      document.getElementById('Slide-' + mediaId)?.remove();
      document.querySelector(`#Slider-Thumbnails-${this.#sectionId} [data-target="${mediaId}"]`)?.remove();
      document.querySelector(`#ProductModal-${this.#sectionId} [data-media-id="${mediaId}"]`)?.remove();
    }

    /** Inserts (if needed) and activates the selected pattern's slide as the new
     *  first slide, via the theme's own MediaGallery.setActiveMedia(id, true) — the
     *  same prepend-to-front mechanism Dawn uses for colour-swatch media swaps —
     *  so the current slide order simply shifts down instead of being replaced. */
    #showPatternMedia(input) {
      const mediaId = input.dataset.mediaId;
      if (!mediaId || !this.#mediaGallery) return;

      if (this.#activePatternMediaId && this.#activePatternMediaId !== mediaId) {
        this.#removeInsertedPatternMedia(this.#activePatternMediaId);
      }

      this.#insertPatternMediaIfNeeded(mediaId);
      // Pass scrollPageIntoView=false: unlike a colour-swatch media swap, this
      // runs from a pattern/size pill click further down the page — the client
      // didn't want that click to yank the page back up to the gallery.
      this.#withMediaGallery((gallery) => gallery.setActiveMedia(mediaId, true, false));
      this.#activePatternMediaId = mediaId;
    }

    /** Removes the inserted pattern slide entirely and reactivates the original
     *  slide in its original (first) position. */
    #restoreOriginalMedia() {
      if (!this.#mediaGallery || !this.#activePatternMediaId) return;
      this.#removeInsertedPatternMedia(this.#activePatternMediaId);
      this.#activePatternMediaId = null;
      if (this.#originalMediaId) {
        const originalMediaId = this.#originalMediaId;
        this.#withMediaGallery((gallery) => gallery.setActiveMedia(originalMediaId, true, false));
      }
    }

    /** Replaces the main price (#variantMainPrice, rendered by
     *  snippets/price-product-detail.liquid) with the combined fabric+pattern
     *  total; restores the original price once nothing is selected. There's
     *  no separate "Total" line in this component — the main price itself is
     *  the running total. */
    #getBundlePrice(radio) {
  if (!radio || !this.#currentFabricVariantId) return null;

  try {
    const prices = JSON.parse(radio.dataset.totalByFabric || '{}');

    return prices[this.#currentFabricVariantId] || null;
  } catch (error) {
    console.error('[product-bundle-patterns] Invalid price data', error);
    return null;
  }
}
    #updatePriceDisplay({ refreshBaseline = false } = {}) {
      if (!this.#mainPriceEl) return;
      if (!this.#originalMainPriceHTML || refreshBaseline) {
        this.#originalMainPriceHTML = this.#mainPriceEl.innerHTML;
      }
      if (!this.#selectedPatternRadio) {
        this.#mainPriceEl.innerHTML = this.#originalMainPriceHTML;
        this.#originalMainPriceHTML = null;
        return;
      }
      const requiresSize = this.#selectedPatternRadio.dataset.hasSizes === 'true';
      let html;
      if (requiresSize) {
  html = this.#selectedSizeRadio
    ? this.#getBundlePrice(this.#selectedSizeRadio)
    : this.#originalMainPriceHTML;
} else {
  html = this.#getBundlePrice(this.#selectedPatternRadio);
}
      this.#mainPriceEl.innerHTML = html || this.#originalMainPriceHTML;
      // The desktop sticky bar shows its own live clone of the price node
      // (see refreshDesktopStickyAddToCart in assets/global.js) — nothing else
      // re-runs that clone when only the price (not the variant/button state)
      // changes, so it has to be nudged here explicitly or it goes stale.
      if (typeof refreshStickyAddToCart === 'function') {
        refreshStickyAddToCart(this.#sectionId);
      }
    }

    #resetSelection() {
      if (this.#selectedPatternRadio) this.#selectedPatternRadio.checked = false;
      this.#selectedPatternRadio = null;
      this.#selectedSizeRadio = null;
      this.#hideAllSizePanels();
      this.#hideSizeError();
      this.#restoreOriginalMedia();
      this.#updatePriceDisplay();
      this.#clearSavedSelection();
    }

    // ── Persistence ───────────────────────────────────────────────────────────
    /** Not scoped to a product id on purpose: a colour switch navigates to a
     *  different product for that colourway, and the selection should still
     *  carry over to it. */
    #saveSelection() {
      if (!this.#selectedPatternRadio) {
        this.#clearSavedSelection();
        return;
      }
      try {
        sessionStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({
            patternHandle: this.#selectedPatternRadio.dataset.handle,
            sizeVariantId: this.#selectedSizeRadio ? this.#selectedSizeRadio.value : null,
          })
        );
      } catch (_) {}
    }

    #clearSavedSelection() {
      try {
        sessionStorage.removeItem(STORAGE_KEY);
      } catch (_) {}
    }

    #restoreSelection() {
      let saved = null;
      try {
        saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
      } catch (_) {
        saved = null;
      }
      if (!saved || !saved.patternHandle) return;

      const input = this.querySelector('input[ref="patternRadios[]"][data-handle="' + saved.patternHandle + '"]');

      if (!input || input.disabled) return;

      input.checked = true;
      this.#selectedPatternRadio = input;
      this.#showPatternMedia(input);
      this.#showSizePanelFor(input);
      if (saved.sizeVariantId) {
        const sizeInput = this.querySelector(
          'input[ref="patternSizeRadios[]"][value="' + saved.sizeVariantId + '"]'
        );
        if (sizeInput && !sizeInput.disabled) {
          sizeInput.checked = true;
          this.#selectedSizeRadio = sizeInput;
        }
      }
      
      this.#updatePriceDisplay();
    }
  }
  if (!customElements.get('product-bundle-patterns')) {
    customElements.define('product-bundle-patterns', ProductBundlePatterns);
  }
})();
